import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Text,
} from '@gousto-internal/citrus-react'

import { DiscountPerWeek } from './components/DiscountPerWeek'
import { Sections, trackCTAClick, TrackingActions } from '../../tracking'
import { DEFAULT_BOXES, TAPERED_DISCOUNT_BOX } from './config'
import { useIsTaperedDiscountCurveEnabled } from '../../../../hooks/useIsTaperedDiscountCurveEnabled'

export type DiscountBannerProps = {
  onBannerCTAClick: () => void
  title?: string
  trackingSection?: Sections
  // The ** points to the "New customers only" footnote, which only landing-page footers render.
  showTermsMarker?: boolean
}

const DefaultDiscountBannerContent = () => {
  const isTaperedDiscountEnabled = useIsTaperedDiscountCurveEnabled()
  const signupDiscountConfig = isTaperedDiscountEnabled ? TAPERED_DISCOUNT_BOX : DEFAULT_BOXES

  return <DiscountPerWeek boxes={signupDiscountConfig} />
}

const onEnter =
  (callback: (event: React.KeyboardEvent) => void) => (event: React.KeyboardEvent) => {
    if (event.code === 'Enter') {
      callback(event)
    }
  }

export function DiscountBanner({
  onBannerCTAClick,
  title = 'Welcome offer',
  trackingSection,
  showTermsMarker = true,
  children,
}: React.PropsWithChildren<DiscountBannerProps>) {
  const handleAndTrackBannerCTAClick = () => {
    onBannerCTAClick()
    trackCTAClick(TrackingActions.COMPREHENSION_GAP_BANNER_CTA, trackingSection ?? Sections.HERO)
  }

  return (
    <Box
      data-testid="discount-banner"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      bg={Color.ColdGrey_900}
      color={Color.White}
      onClick={handleAndTrackBannerCTAClick}
      onKeyDown={onEnter(handleAndTrackBannerCTAClick)}
      role="button"
      aria-label="Apply welcome offer discount"
      tabIndex={0}
      width={['min(640px, calc(100% - 32px))', 'fit-content', 'fit-content']}
      maxWidth={['none', 'calc(100% - 48px)', 'none']}
      paddingH={[3, 4, 8]}
      paddingV={[3, 4, 5]}
      style={{
        borderRadius: '8px',
        backgroundColor: 'rgba(33, 41, 48, 0.5)',
        backdropFilter: 'blur(16px)',
      }}
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        flexDirection={FlexDirection.Column}
        gap={[1, 2]}
      >
        <Text fontWeight={FontWeight.Bold} size={2} color={Color.White}>
          {title}
          {showTermsMarker && <sup>**</sup>}
        </Text>
        {children ?? <DefaultDiscountBannerContent />}
      </Box>
    </Box>
  )
}
